import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import ModalBackdrop from "@/components/ModalBackdrop";
import { ContextSelect } from "./ContextSelect";
import { ValidityFields, metaPatch } from "./ValidityFields";
import { ValidityChip } from "./ValidityChip";
import { useContextVocab } from "./vocab";

const TYPED = { protocol: "protocol", cta: "cta", budget: "budget", study_build: "study_build" };

export function MetadataModal({ item, onClose, onSaved, endpoint = "/admin/datalake/items" }) {
    const { active, byId, irbRelevant } = useContextVocab();
    const [contexts, setContexts] = useState(item.contexts || []);
    const [meta, setMeta] = useState(item.doc_meta || {});
    const [saving, setSaving] = useState(false);
    const locked = TYPED[item.source_kind] ? [TYPED[item.source_kind]] : [];
    const options = active.concat((item.contexts || []).filter((id) => byId[id] && !byId[id].active).map((id) => byId[id]));

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            if (JSON.stringify(contexts) !== JSON.stringify(item.contexts || [])) body.contexts = contexts;
            const patch = metaPatch(meta, item.doc_meta);
            if (Object.keys(patch).length) body.doc_meta = patch;
            if (!Object.keys(body).length) { toast.info("Nothing changed"); onClose(); return; }
            const { data } = await api.patch(`${endpoint}/${item.id}`, body);
            toast.success(`Saved · ${data.validity_state?.replace("_", " ") || "updated"}`);
            onSaved(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalBackdrop onClose={onClose}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="dl-metadata-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 sticky top-0 bg-[#0a0a0a] z-10">
                    <div>
                        <div className="font-display text-lg tracking-tight">Contexts &amp; validity</div>
                        <div className="text-[11px] text-white/45 font-mono truncate max-w-[420px]">{item.title || item.document_name || item.id}</div>
                    </div>
                    <ValidityChip state={item.validity_state} meta={item.doc_meta} itemId={`modal-${item.id}`} showDate />
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white" data-testid="dl-metadata-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <ContextSelect value={contexts} onChange={setContexts} options={options} locked={locked} testId="dl-metadata-contexts" />
                    {locked.length > 0 && <div className="text-[11px] text-white/40">Typed documents always keep their own context ({locked.join(", ")}).</div>}
                    <ValidityFields value={meta} onChange={setMeta} testId="dl-metadata-validity"
                        showIrb={irbRelevant(contexts) || ["irb_approved", "irb_approval_date", "irb_name"].some((k) => item.doc_meta?.[k] !== undefined && item.doc_meta?.[k] !== null)}
                        irbHint={`IRB approval fields do not apply to this ${item.source_kind && item.source_kind !== "document" ? item.source_kind.toUpperCase() : "document"} — they appear only for IRB-relevant contexts (ICF, protocol, regulatory, safety).`} />
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                        <button type="button" onClick={save} disabled={saving} data-testid="dl-metadata-save"
                            className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </ModalBackdrop>
    );
}
